/**
 * Shared building blocks for the screens migrated to the new backend, so they
 * look like the rest of the app: white panels on the grey page, one border
 * colour, one set of status colours.
 */

/** Status colours, shared by the lists and the detail pages. */
export const ORDER_STATUS_STYLES: Record<string, { bg: string; text: string }> =
  {
    draft: { bg: "#e7e7e7", text: "#595959" },
    quoted: { bg: "#fff6b6", text: "#af7e04" },
    confirmed: { bg: "#c6dcff", text: "#305bab" },
    processing: { bg: "#dedaff", text: "#6631d7" },
    fulfilled: { bg: "#adf0c7", text: "#067429" },
    on_hold: { bg: "#f8d3af", text: "#9b4a08" },
    cancelled: { bg: "#ffc6c6", text: "#bd0a0a" },
    refunded: { bg: "#ffc6c6", text: "#bd0a0a" },
  };

export const PAYMENT_STATUS_STYLES: Record<
  string,
  { bg: string; text: string }
> = {
  received: { bg: "#adf0c7", text: "#067429" },
  partially_received: { bg: "#fff6b6", text: "#af7e04" },
  pending: { bg: "#e7e7e7", text: "#595959" },
  failed: { bg: "#ffc6c6", text: "#bd0a0a" },
};

export const LIFECYCLE_STYLES: Record<string, { bg: string; text: string }> = {
  lead: { bg: "#fff6b6", text: "#af7e04" },
  prospect: { bg: "#c6dcff", text: "#305bab" },
  customer: { bg: "#adf0c7", text: "#067429" },
  inactive: { bg: "#e7e7e7", text: "#595959" },
};

/** Organizations and people are simply on or off. */
export const ACTIVE_STYLES: Record<string, { bg: string; text: string }> = {
  active: { bg: "#adf0c7", text: "#067429" },
  inactive: { bg: "#e7e7e7", text: "#595959" },
  disabled: { bg: "#e7e7e7", text: "#595959" },
  suspended: { bg: "#ffc6c6", text: "#bd0a0a" },
};

export const LEAD_STAGE_STYLES: Record<string, { bg: string; text: string }> = {
  new: { bg: "#c6dcff", text: "#305bab" },
  contacted: { bg: "#dedaff", text: "#6631d7" },
  quoted: { bg: "#fff6b6", text: "#af7e04" },
  won: { bg: "#adf0c7", text: "#067429" },
  lost: { bg: "#ffc6c6", text: "#bd0a0a" },
};

export function StatusPill({
  value,
  palette = ORDER_STATUS_STYLES,
}: {
  value: string;
  palette?: Record<string, { bg: string; text: string }>;
}) {
  const style = palette[value] ?? { bg: "#e7e7e7", text: "#595959" };
  return (
    <span
      style={{ backgroundColor: style.bg, color: style.text }}
      className="whitespace-nowrap rounded-md px-2 py-[3px] text-sm font-medium capitalize"
    >
      {value.replace(/_/g, " ")}
    </span>
  );
}

/** A white panel, the app's standard surface for a block of content. */
export function Card({
  title,
  action,
  children,
  className = "",
}: {
  title?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section
      className={`rounded-xl border border-gray-border-gray-lite bg-white-A700 p-5 ${className}`}
    >
      {(title || action) && (
        <div className="mb-4 flex items-center justify-between gap-3">
          {title && (
            <h2 className="text-sm font-semibold uppercase tracking-wide text-form-content-label">
              {title}
            </h2>
          )}
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

export function Field({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs uppercase tracking-wide text-form-content-label">
        {label}
      </span>
      <span className="text-base text-black-label">{value || "-"}</span>
    </div>
  );
}

/** A metric tile for the dashboards. */
export function Tile({
  label,
  value,
  hint,
  onClick,
}: {
  label: string;
  value: string;
  hint?: string;
  onClick?: () => void;
}) {
  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      className="flex flex-col items-start gap-1 rounded-xl border border-gray-border-gray-lite bg-white-A700 p-5 text-left enabled:hover:border-blue-700"
    >
      <span className="text-xs uppercase tracking-wide text-form-content-label">
        {label}
      </span>
      <span className="text-2xl font-semibold text-black-label">{value}</span>
      {hint && <span className="text-sm text-form-content-label">{hint}</span>}
    </button>
  );
}

/** Page wrapper: the migrated detail screens sit on the app's grey background. */
export function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full w-full flex-col gap-5 overflow-auto p-5 font-inter sm:p-3">
      {children}
    </div>
  );
}
